import React, { useCallback, useState } from 'react'

import { SxProps } from '@mui/material'

import { TCustomizedMetric } from 'types/GlobalMetric'
import { Context } from 'types/GlobalUserEvents'

import { useGroupActions } from 'redux/actions/Groups'
import { useCustomer } from 'redux/hooks/Customer'
import { useDashboardGroupsInGroup } from 'redux/hooks/DashboardGroups'
import {
  useGroup,
  useIsAddingMetricToGroup,
  useIsOwner,
  useMember
} from 'redux/hooks/Groups'
import { useMetrics } from 'redux/hooks/Metrics'
import { useAccountSettings } from 'redux/hooks/SignedInAccount'
import { useIsMobile, useParams, useResources } from 'utils/hooks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import ButtonField from 'components_new/molecules/ButtonField'
import Emoji from 'components_new/atoms/Emoji'
import Grid from 'components_new/atoms/Grid'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SectionHeader from 'components_new/molecules/SectionHeader'

import ResourcesTable from 'components_new/organisms/ResourcesTable'
import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import MetricsDialog from 'components_new/organisms/dialogs/MetricsDialog'
import MetricPreview, {
  METRIC_PREVIEW_HEIGHT
} from 'components_new/organisms/MetricPreview'
import KeyMetricsTree from 'components_new/organisms/KeyMetricsTree'
import MetricViewDialog from 'components_new/organisms/dialogs/MetricViewDialog'

import Loading from './loading'
import Locked from './locked'
interface GroupContentProps {
  editMode: boolean
  setEditMode: React.Dispatch<React.SetStateAction<boolean>>
  sx?: SxProps
}

const GroupContent = (props: GroupContentProps) => {
  const { editMode, setEditMode, sx } = props
  const { id } = useParams<{ id: string }>()
  const { t, lang } = useTranslation()

  const customer = useCustomer()
  const group = useGroup(id)
  const groupMember = useMember(id)
  const { isAdmin } = useAccountSettings()
  const isMobile = useIsMobile()
  const isOwner = useIsOwner(id)
  const isAddingMetric = useIsAddingMetricToGroup()
  const [metrics] = useMetrics()

  const dashboardGroups = useDashboardGroupsInGroup(id)
  const resources = useResources({
    dashboardGroups,
    reports: [] // $TODO
  })

  const { tryAddMetricToGroup, tryRemoveMetricFromGroup } = useGroupActions()

  const [dialog, setDialog] = useState<{
    metric: TCustomizedMetric | null
    variant: 'add' | 'delete' | 'preview'
  } | null>(null)

  const handleClose = () => setDialog(null)

  // add
  const handleAdd = useCallback(() => {
    setDialog({
      metric: null,
      variant: 'add'
    })
  }, [])

  // delete
  const handleDelete = useCallback((metric: TCustomizedMetric) => {
    setDialog({
      metric: metric,
      variant: 'delete'
    })
  }, [])

  // preview
  const handlePreview = useCallback((metric: TCustomizedMetric) => {
    setDialog({
      metric: metric,
      variant: 'preview'
    })
  }, [])

  /*-- loading --*/
  if (!group) return <Loading sx={sx} />

  /*-- locked --*/
  const isLocked = group.is_private && !groupMember && !isAdmin

  if (isLocked) return <Locked sx={sx} />

  /*-- success --*/
  return (
    <>
      <Box sx={sx}>
        {customer?.allow_access.allow_key_metrics ? (
          <Box
            sx={{
              mb: 6
            }}
          >
            <SectionHeader
              gutterBottom={true}
              helperText={t('groups', 'key_metrics_helper_text')}
              icon={{
                name: 'KeyMetrics'
              }}
              size="medium"
              title={t('key_metrics', 'key_metrics')}
            />

            {group.key_metrics.length === 0 ? (
              <EmptyPlaceholder
                actions={
                  <Button href="/key-metrics">
                    {t('key_metrics', 'to_key_metrics')}
                  </Button>
                }
                background={true}
                body={t('groups', 'no_key_metrics_body')}
                size="small"
                title={
                  <>
                    {t('groups', 'no_key_metrics_title')}{' '}
                    <Emoji name="seedling" />
                  </>
                }
              />
            ) : (
              <>
                <KeyMetricsTree
                  editMode={editMode}
                  groupId={group.id}
                  keyMetrics={group.key_metrics}
                />
              </>
            )}
          </Box>
        ) : null}

        <Box
          sx={{
            mb: 6,
            ...(editMode
              ? {
                  cursor: 'not-allowed',
                  opacity: 0.5,
                  pointerEvents: 'none'
                }
              : null)
          }}
        >
          <SectionHeader
            gutterBottom={true}
            helperText={t('groups', 'resources_helper_text')}
            icon={{
              name: 'Resources'
            }}
            size="medium"
            title={t('global', 'resources')}
            to={`/groups/${group.id}/resources`}
          />
          {dashboardGroups.length === 0 ? (
            <EmptyPlaceholder
              actions={
                isOwner ? (
                  <Button href="/dashboards">
                    {t('dashboards', 'to_dashboards')}
                  </Button>
                ) : null
              }
              background={true}
              body={isOwner ? t('groups', 'no_dashboards_body') : ''}
              size="small"
              title={
                <>
                  {t('groups', 'no_dashboards_title')} <Emoji name="seedling" />
                </>
              }
            />
          ) : (
            <ResourcesTable
              context={Context.FROM_GROUP_RESOURCES_PREVIEW}
              items={resources}
              preview={true}
              viewAllHref={`/groups/${group.id}/resources`}
            />
          )}
        </Box>

        {customer?.allow_access.allow_key_metrics ? (
          <Box sx={{ mb: 6 }}>
            <SectionHeader
              gutterBottom={true}
              helperText={t('groups', 'metrics_helper_text')}
              icon={{
                name: 'Metric'
              }}
              size="medium"
              title={t('metrics', 'metrics')}
            />

            {group.metrics.length === 0 && !editMode ? (
              <EmptyPlaceholder
                actions={
                  isOwner && !isMobile ? (
                    <Button onClick={() => setEditMode(true)}>
                      {t('global', 'edit')}
                    </Button>
                  ) : null
                }
                background={true}
                body={isOwner ? t('groups', 'no_metrics_body') : ''}
                size="small"
                title={
                  <>
                    {t('groups', 'no_metrics_title')} <Emoji name="seedling" />
                  </>
                }
              />
            ) : (
              <Grid container={true} spacing={2}>
                {group.metrics.map((groupMetric) => (
                  <Grid item={true} key={groupMetric.id} xs={12} sm={6}>
                    <MetricPreview
                      editMode={editMode}
                      id={groupMetric.metric.id}
                      onView={() => handlePreview(groupMetric.metric)}
                      options={[
                        {
                          color: 'error',
                          iconName: 'Delete',
                          onClick: () => handleDelete(groupMetric.metric),
                          title: t('metrics', 'delete_metric')
                        }
                      ]}
                      settings={groupMetric.metric.settings}
                    />
                  </Grid>
                ))}

                {editMode ? (
                  <Grid item={true} xs={12} sm={6}>
                    <ButtonField
                      editMode={true}
                      fullHeight={true}
                      fullWidth={true}
                      onClick={handleAdd}
                      sx={{ height: METRIC_PREVIEW_HEIGHT }}
                      title={`${t('global', 'add')} ${t(
                        'metrics',
                        'metric',
                        true
                      )}`}
                    />
                  </Grid>
                ) : null}
              </Grid>
            )}
          </Box>
        ) : null}
      </Box>

      {/*-- dialogs --*/}

      {/*-- delete --*/}
      <DeleteDialog
        onClose={handleClose}
        onDelete={(cb) => {
          const groupMetricId = group.metrics.find(
            (m) => m.metric.id === dialog?.metric?.id
          )?.id

          if (groupMetricId) {
            tryRemoveMetricFromGroup(group.id, groupMetricId)
          }

          handleClose()
          cb()
        }}
        open={dialog?.variant === 'delete'}
        title={
          metrics.find((m) => m.id === dialog?.metric?.id)?.title[lang] ?? ''
        }
        type={t('metrics', 'metric', true)}
        unlockPhrase={t('global', 'delete')}
      />

      {/*-- add --*/}
      <MetricsDialog
        availableSteps={['variant', 'slices', 'distribution', 'rollingSum']}
        creating={isAddingMetric}
        handleOnAdd={(metric, settings) =>
          tryAddMetricToGroup(
            group.id,
            {
              kpi_template_id: metric.id,
              ...settings
            },
            (err) => {
              if (!err) handleClose()
            }
          )
        }
        onClose={handleClose}
        open={dialog?.variant === 'add'}
        variant="metrics"
        title={t('metrics', 'add_metric')}
      />

      {/*-- preview --*/}
      <MetricViewDialog
        metric={dialog?.metric ?? null}
        onClose={handleClose}
        open={dialog?.variant === 'preview'}
      />
    </>
  )
}

export default GroupContent
